/**
 * governance/utils.ts
 * Consolidated governance utilities: severity levels, patterns, fix suggestions.
 * Single source of truth — replaces the separate patterns.ts, severity.ts, fixSuggestions.ts files.
 */

// ─── Severity ────────────────────────────────────────────────────────────────

/** Canonical severity type — 3 levels only, no 'error' */
export type SeverityLevel = 'critical' | 'warning' | 'info'

/** Alias used by unified validation pipeline */
export type GovernanceSeverity = SeverityLevel

export const SEVERITY_ORDER: Record<SeverityLevel, number> = {
  critical: 0,
  warning: 1,
  info: 2,
}

export function compareSeverity(a: SeverityLevel, b: SeverityLevel): number {
  return SEVERITY_ORDER[a] - SEVERITY_ORDER[b]
}

export function isBlockingSeverity(level: SeverityLevel): boolean {
  return level === 'critical'
}

export function severityLabel(level: SeverityLevel): string {
  const labels: Record<SeverityLevel, string> = {
    critical: '🔴 Critical',
    warning: '🟡 Warning',
    info: '🔵 Info',
  }
  return labels[level]
}

export function severityColor(level: SeverityLevel): string {
  const colors: Record<SeverityLevel, string> = {
    critical: '#ef4444',
    warning: '#eab308',
    info: '#3b82f6',
  }
  return colors[level]
}

/** Penalty weights per severity — STRENGTHENED */
export const SEVERITY_WEIGHTS: Record<SeverityLevel, number> = {
  critical: 30, // was 25 — criticals are now more punishing
  warning: 12, // was 10
  info: 3,
}

/** Normalize any legacy severity string to canonical SeverityLevel */
export function normalizeSeverity(raw: string | undefined): SeverityLevel {
  if (raw === 'critical' || raw === 'error' || raw === 'high') return 'critical'
  if (raw === 'warning' || raw === 'medium') return 'warning'
  return 'info'
}

// ─── Patterns ────────────────────────────────────────────────────────────────

export interface GovernancePattern {
  id: string
  name: string
  description: string
  severity: SeverityLevel
  category:
    | 'structure'
    | 'taxonomy'
    | 'visual'
    | 'answer'
    | 'didactic'
    | 'metadata'
  check: (question: Record<string, unknown>) => boolean
  message: (question: Record<string, unknown>) => string
}

/** Registry of all governance patterns used across validators */
export const GOVERNANCE_PATTERNS: GovernancePattern[] = [
  {
    id: 'missing-family-id',
    name: 'Missing familyId',
    description: 'Question must have a familyId linking it to the taxonomy',
    severity: 'critical',
    category: 'taxonomy',
    check: (q) =>
      !q.familyId ||
      typeof q.familyId !== 'string' ||
      (q.familyId as string).trim() === '',
    message: () => 'Question is missing a valid familyId',
  },
  {
    id: 'missing-skill-id',
    name: 'Missing skillId',
    description: 'Question must have a skillId',
    severity: 'critical',
    category: 'taxonomy',
    check: (q) =>
      !q.skillId ||
      typeof q.skillId !== 'string' ||
      (q.skillId as string).trim() === '',
    message: () => 'Question is missing a valid skillId',
  },
  {
    id: 'missing-question-text',
    name: 'Missing question text',
    description: 'Question must have non-empty question text',
    severity: 'critical',
    category: 'structure',
    check: (q) =>
      !q.question ||
      typeof q.question !== 'string' ||
      (q.question as string).trim() === '',
    message: () => 'Question text is empty or missing',
  },
  {
    id: 'missing-answer',
    name: 'Missing answer',
    description: 'Question must have a defined correct answer',
    severity: 'critical',
    category: 'answer',
    check: (q) =>
      q.answer === undefined || q.answer === null || q.answer === '',
    message: () => 'Question is missing a correct answer',
  },
  {
    id: 'missing-type',
    name: 'Missing question type',
    description: 'Question must have a valid type field',
    severity: 'critical',
    category: 'structure',
    check: (q) => !q.type || typeof q.type !== 'string',
    message: () => 'Question is missing a type field',
  },
  {
    id: 'missing-difficulty',
    name: 'Missing difficulty',
    description: 'Question should have a difficulty level',
    severity: 'warning',
    category: 'metadata',
    check: (q) => q.difficulty === undefined || q.difficulty === null,
    message: () => 'Question is missing a difficulty level',
  },
  {
    id: 'missing-explanation',
    name: 'Missing explanation',
    description: 'Question should have an explanation for didactic quality',
    severity: 'warning',
    category: 'didactic',
    check: (q) =>
      !q.explanation ||
      typeof q.explanation !== 'string' ||
      (q.explanation as string).trim() === '',
    message: () => 'Question is missing an explanation',
  },
  {
    id: 'visual-answer-leakage',
    name: 'Visual answer leakage',
    description: 'Visual data should not contain the answer',
    severity: 'critical',
    category: 'visual',
    check: (q) => {
      if (!q.visual || typeof q.visual !== 'object') return false
      const visual = q.visual as Record<string, unknown>
      const answer = String(q.answer ?? '')
        .toLowerCase()
        .trim()
      if (!answer) return false
      const visualStr = JSON.stringify(visual).toLowerCase()
      // Heuristic: if the answer string appears verbatim in visual data label fields
      return (
        visualStr.includes(`"label":"${answer}"`) ||
        visualStr.includes(`"value":"${answer}"`)
      )
    },
    message: (q) => `Visual data may leak the answer "${q.answer}"`,
  },
]

export function getPatternById(id: string): GovernancePattern | undefined {
  return GOVERNANCE_PATTERNS.find((p) => p.id === id)
}

export function getPatternsByCategory(
  category: GovernancePattern['category'],
): GovernancePattern[] {
  return GOVERNANCE_PATTERNS.filter((p) => p.category === category)
}

export function getPatternsBySeverity(
  severity: SeverityLevel,
): GovernancePattern[] {
  return GOVERNANCE_PATTERNS.filter((p) => p.severity === severity)
}

// ─── Fix Suggestions ─────────────────────────────────────────────────────────

export interface FixSuggestion {
  patternId: string
  title: string
  description: string
  autoFixable: boolean
  steps: string[]
}

export const FIX_SUGGESTIONS: Record<string, FixSuggestion> = {
  'missing-family-id': {
    patternId: 'missing-family-id',
    title: 'Add familyId',
    description: 'Set the familyId field to match a registered taxonomy family',
    autoFixable: false,
    steps: [
      'Open the question seed file',
      'Add a "familyId" field matching a key in taxonomyRegistry',
      'Run validation to confirm the fix',
    ],
  },
  'missing-skill-id': {
    patternId: 'missing-skill-id',
    title: 'Add skillId',
    description:
      'Set the skillId field to match a registered skill within the family',
    autoFixable: false,
    steps: [
      'Open the question seed file',
      'Add a "skillId" field matching a skill in the family',
      'Run validation to confirm the fix',
    ],
  },
  'missing-question-text': {
    patternId: 'missing-question-text',
    title: 'Add question text',
    description: 'Provide a non-empty question string',
    autoFixable: false,
    steps: [
      'Open the question seed file',
      'Add a "question" field with the question text',
    ],
  },
  'missing-answer': {
    patternId: 'missing-answer',
    title: 'Add correct answer',
    description: 'Provide the correct answer in the "answer" field',
    autoFixable: false,
    steps: [
      'Open the question seed file',
      'Add an "answer" field with the correct answer value',
    ],
  },
  'missing-type': {
    patternId: 'missing-type',
    title: 'Add question type',
    description: 'Set the "type" field to a registered question type',
    autoFixable: false,
    steps: [
      'Open the question seed file',
      'Add a "type" field (e.g. "multiple-choice", "fill-in", "ordering")',
      'Ensure the type is registered in questionTypeRegistry',
    ],
  },
  'missing-difficulty': {
    patternId: 'missing-difficulty',
    title: 'Add difficulty level',
    description: 'Set the "difficulty" field to 1, 2, or 3',
    autoFixable: true,
    steps: [
      'Add "difficulty": 1 as a default, then adjust based on question complexity',
    ],
  },
  'missing-explanation': {
    patternId: 'missing-explanation',
    title: 'Add explanation',
    description: 'Provide a clear explanation of the correct answer',
    autoFixable: false,
    steps: [
      'Open the question seed file',
      'Add an "explanation" field with a clear, educational explanation',
    ],
  },
  'visual-answer-leakage': {
    patternId: 'visual-answer-leakage',
    title: 'Fix visual answer leakage',
    description: 'Remove or obfuscate the answer from visual data',
    autoFixable: false,
    steps: [
      'Review the visual data structure',
      'Ensure labels/values in visual data do not directly reveal the answer',
      'Use neutral labels or restructure the visual',
    ],
  },
}

export function getFixSuggestion(patternId: string): FixSuggestion | undefined {
  return FIX_SUGGESTIONS[patternId]
}

export function getAutoFixablePatterns(): FixSuggestion[] {
  return Object.values(FIX_SUGGESTIONS).filter((s) => s.autoFixable)
}

// ─── Didactic / Pattern helpers ───────────────────────────────────────────────

/** Patterns that indicate a generic, boilerplate explanation */
export const GENERIC_EXPLANATION_PATTERNS: RegExp[] = [
  /^het goede antwoord is/i,
  /^het antwoord is/i,
  /^dit is (het )?correct/i,
  /^correct[.!]?\s*$/i,
  /^goed[.!]?\s*$/i,
  /^fout[.!]?\s*$/i,
  /^zie uitleg/i,
  /^kijk naar/i,
  /^let op[.!]?\s*$/i,
  /^antwoord:/i,
  // NEW: Additional generic patterns that slip through
  /^het juiste antwoord/i,
  /^dit antwoord is/i,
  /^kijk rustig naar de vraag/i,
  /^werk stap voor stap\.\s*zo zie je/i,
  /^zo zie je waarom/i,
  /^vergelijk met het juiste antwoord/i,
  /^let op de belangrijkste aanwijzing/i,
  /^denk nog eens goed na/i,
  /^lees de vraag nog eens/i,
  /^pas niet precies bij de vraag/i,
  /^nog niet goed\./i,
  /^het antwoord is niet juist/i,
  /^probeer het opnieuw/i,
  /^dat is (niet )?juist/i,
  /^helaas/i,
  /^jammer/i,
  /^goed gedaan[.!]?\s*$/i,
  /^prima[.!]?\s*$/i,
  /^klopt[.!]?\s*$/i,
  // v5.8: Forbidden explanation openers from CONTENT_GOVERNANCE_FRAMEWORK §10.4
  /^strategie\s*:/i,
  /^regel\s*:/i,
  /^stap\s*1\s*:/i,
  /^misconceptie\s*:/i,
  /^tip\s*:/i,
]

/** Patterns that indicate a generic distractor explanation */
export const GENERIC_DISTRACTOR_PATTERNS: RegExp[] = [
  /^dit is (niet )?correct/i,
  /^fout antwoord/i,
  /^verkeerd/i,
  /^niet goed/i,
  /^onjuist[.!]?\s*$/i,
  /^probeer opnieuw/i,
  /^dat klopt niet/i,
  // NEW: Additional generic distractor patterns
  /^dit is niet het goede antwoord/i,
  /^helaas, dit is niet/i,
  /^dit antwoord is fout/i,
  /^niet het goede antwoord/i,
  /^kijk nog eens/i,
  /^pas niet bij/i,
  /^verkeerd antwoord/i,
  /^nee,?\s*(dit is)?\s*(niet)?\s*(correct|goed|juist)/i,
  /^dit kan niet/i,
  // REMOVED v5.11: "bijna, maar" and "net niet" — these are valid Dutch
  // pedagogical openers, not generic filler. "Bijna, maar..." acknowledges
  // the student's attempt before correcting, which is good pedagogy.
  // Previously: /^bijna,?\s*maar/i, /^net niet/i
  // NEW from CONTENT_GOVERNANCE_FRAMEWORK §2.1 — explicitly forbidden openers
  /^veelgemaakte fout/i,
  /^helaas[,.]?\s*$/i,
  /^jammer[,.]?\s*$/i,
  /^probeer het opnieuw/i,
  /^kijk nog eens naar de vraag/i,
  // v5.8: Additional forbidden distractor openers from calibration work
  /^fout\s*:/i,
  /^correct\s*!/i,
  /^correct\s*\./i,
  /^strategie\s*:/i,
  /^misconceptie\s*:/i,
  /^regel\s*:/i,
  /^tip\s*:/i,
]

/**
 * Forbidden opener patterns — shared between explanation and distractor checks.
 * Used by contentStage.ts for the CONTENT_FORBIDDEN_OPENER issue.
 * Deduplicated from CQ-2 + CQ-8 (was 3 locations → 1 constant).
 */
export const FORBIDDEN_OPENER_PATTERNS: ReadonlyArray<{
  pattern: RegExp
  label: string
}> = [
  { pattern: /^strategie\s*:/i, label: 'strategie:' },
  { pattern: /^veelgemaakte fout/i, label: 'veelgemaakte fout' },
  { pattern: /^misconceptie\s*:/i, label: 'misconceptie:' },
  { pattern: /^tip\s*:/i, label: 'tip:' },
] as const

/** Module-specific misconception patterns */
export const GET_MISCONCEPTIONS =
  /plaatswaarde|komma|afrond|decimaal|breuk|noemer|teller|macht|wortel|procent|verhouding/i
export const SWW_RULES =
  /stam|kofschip|dt-regel|verdubbel|verenkeld|persoonsvorm|infinitief|voltooid deelwoord/i
export const SWW_CHECK_STRATEGY = /ik-vorm|hij-vorm|infinitief|stam\b/i
export const SPR_RULES =
  /open lettergreep|gesloten lettergreep|trema|koppelteken|tussen-n|samengesteld/i
export const MET_FORMULA =
  /formule|oppervlakte|omtrek|inhoud|π|pi|\bL\s*[×x\*]\s*B\b|kwadraat|kubiek|omrekenen|eenheid/i
export const TGL_CALC = /\d+\s*[×x\*÷\/\+\-]\s*\d+/
export const TGL_ERRORS =
  /eenheid|minuten|uren|seconden|euro|cent|wisselgeld|tijdzone|notatie/i
export const TAA_CITE =
  /tekst|alinea|zin|regel|grammatica|werkwoord|zelfstandig naamwoord|bijvoeglijk|lidwoord/i
export const STU_STRATEGY =
  /strategie|samenvatten|skimmen|scannen|hoofd(gedachte|idee)|steekwoord|mindmap|schema/i

/**
 * Returns true if the text matches any of the given generic patterns.
 * FIXED: For explanation patterns, texts that start with a generic opener but
 * continue with substantive didactic content (>60 chars + didactic markers)
 * are NOT flagged as generic. This prevents false positives on the common
 * Dutch pedagogical pattern "Het goede antwoord is X. [detailed explanation]."
 */
export function isGenericText(text: string, patterns: RegExp[]): boolean {
  const trimmed = text.trim()
  if (!patterns.some((p) => p.test(trimmed))) return false

  // If the text is short, it's genuinely generic — flag it
  if (trimmed.length <= 60) return true

  // For longer texts: check if the matched pattern is an "opener" pattern
  // (starts with ^) that could be followed by substantive content.
  // If the text has real didactic substance after the opener, don't flag it.
  const isOpenerMatch = patterns.some((p) => {
    const source = p.source
    // Patterns that match the START of text (openers) vs patterns that match
    // the WHOLE text (with $ anchor) or specific phrases anywhere
    return source.startsWith('^') && !source.includes('$') && p.test(trimmed)
  })

  if (isOpenerMatch) {
    // The text starts with a generic opener but is long — check for substance
    // If it has didactic quality, it's NOT generic (the opener is just a preamble)
    if (isDidactic(trimmed)) return false

    // Also check for module-agnostic substance markers
    const hasSubstance =
      // Contains a rule or principle reference
      /regel:|formule:|stap\s*\d|omdat|want|dus\b.*[.!]/i.test(trimmed) ||
      // Contains mathematical notation or comparison
      /[<>≤≥≠]=?\s*-?\d/.test(trimmed) ||
      /\d+\s*[×x\*÷\/\+\-]\s*\d+/.test(trimmed) ||
      // Contains structured reasoning (multiple sentences with content)
      trimmed.split(/[.!]/).filter((s) => s.trim().length > 15).length >= 2

    if (hasSubstance) return false
  }

  return true
}

/**
 * Returns true if the text shows step-by-step or didactic reasoning.
 * FIXED v2.2: Recognizes rule references ("Regel:"), comparison operators
 * (<, >, ≤, ≥), Dutch comparative reasoning ("hoe...hoe"), and
 * conclusion patterns ("dus" + mathematical content).
 *
 * FIXED v5.10: Expanded to recognize common Dutch pedagogical patterns:
 * - Question-answer reasoning: "Is X ≥ 5? Ja — dus..."
 * - Identification + conclusion: "dat is de X ... dus"
 * - "Kijk naar" + specific identification
 * - "Onthoud:" / "Controleer:" / "Check:" patterns
 * - Arrow notation "→" for transformation steps
 * - "Je dacht waarschijnlijk" misconception addressing
 * - Enumerated reasoning with ":" colons
 */
export function isDidactic(text: string): boolean {
  const trimmed = text.trim()
  // Must have at least 25 chars to be considered didactic
  if (trimmed.length < 25) return false

  // Strong didactic signals: actual calculation or multi-step reasoning
  const hasCalculation = /\d+\s*[×x\*÷\/\+\-]\s*\d+\s*=\s*\d+/.test(trimmed)
  const hasStepStructure =
    /stap\s*\d|eerst.*dan|vervolgens.*dan|1\.\s|2\.\s|a\)\s|b\)\s/i.test(
      trimmed,
    )
  const hasCausalReasoning =
    /omdat.*daarom|want.*dus|als.*dan|doordat|hierdoor|waardoor/i.test(trimmed)
  // FIXED: Also recognize standalone "Regel:" and "regel:" (not just "de regel")
  const hasExplicitRule =
    /\bregel\s*:|de regel|de formule|de wet|het principe|de methode/i.test(
      trimmed,
    )
  // NEW: Recognize mathematical comparisons (e.g. "-5 < -2", "3 > 1")
  const hasComparison =
    /-?\d+\s*[<>≤≥≠]\s*-?\d+/.test(trimmed) ||
    /kleiner dan|groter dan|gelijk aan|meer dan|minder dan/i.test(trimmed)
  // NEW: Recognize Dutch comparative reasoning ("hoe verder...hoe kleiner")
  const hasComparativeReasoning = /hoe\s+\w+.*hoe\s+\w+/i.test(trimmed)
  // NEW: Recognize conclusion with mathematical content ("Dus -5 < -2")
  const hasConclusionWithMath =
    /\bdus\b.*-?\d/i.test(trimmed) && trimmed.length >= 40

  // v5.10: NEW strong signals for Dutch pedagogical patterns
  // Question-answer reasoning: "Is 7 ≥ 5? Ja" or "Is 3 kleiner dan 5? Ja"
  const hasQuestionAnswer = /\b(is|zijn|was|wordt)\b.+\?\s*(ja|nee)/i.test(
    trimmed,
  )
  // Arrow notation for transformation steps: "3,47 → 3,5"
  const hasArrowTransform = /→/.test(trimmed) && /\d/.test(trimmed)
  // Identification + conclusion: "dat is de 7" + "dus" somewhere
  const hasIdentifyAndConclude =
    /dat is (de|het|een)\s+\w+/i.test(trimmed) && /\bdus\b/i.test(trimmed)
  // Pedagogical directives with specific content
  const hasPedagogicalDirective =
    /\b(onthoud|controleer|check|pas op|let op)\s*:/i.test(trimmed)
  // Misconception addressing: "Je dacht waarschijnlijk"
  const hasMisconceptionAddress = /je dacht (waarschijnlijk|misschien)/i.test(
    trimmed,
  )
  // "Kijk naar" + specific identification (not just "kijk naar de vraag")
  const hasDirectedLook =
    /kijk naar (het|de|een)\s+\w+.*:\s*(de|het|dat|die|\d)/i.test(trimmed)

  if (
    hasCalculation ||
    hasStepStructure ||
    hasCausalReasoning ||
    hasExplicitRule ||
    hasComparison ||
    hasComparativeReasoning ||
    hasConclusionWithMath ||
    hasQuestionAnswer ||
    hasArrowTransform ||
    hasIdentifyAndConclude ||
    hasPedagogicalDirective ||
    hasMisconceptionAddress ||
    hasDirectedLook
  )
    return true

  // Weaker signals need at least 2 to count
  let weakSignals = 0
  if (/omdat|want|dus|daarom/i.test(trimmed)) weakSignals++
  if (/stap|eerst|dan|vervolgens/i.test(trimmed)) weakSignals++
  // FIXED: Also count comparison operators as a signal
  if (trimmed.includes('=') || /[<>≤≥≠]/.test(trimmed)) weakSignals++
  if (/\d+\s*[×x\*÷\/\+\-]\s*\d+/.test(trimmed)) weakSignals++
  // NEW: "links/rechts" on number line is a didactic signal
  if (/links.*rechts|rechts.*links|getallenlijn/i.test(trimmed)) weakSignals++
  // v5.10: Additional weak signals
  if (/→/.test(trimmed)) weakSignals++
  if (/onthoud|controleer|check/i.test(trimmed)) weakSignals++
  if (/je dacht/i.test(trimmed)) weakSignals++
  // "wordt" used as transformation (not passive): "3,47 wordt 3,5"
  if (/\d.*\bwordt\b.*\d/i.test(trimmed)) weakSignals++

  return weakSignals >= 2
}

/**
 * Returns true if the explanation is substantive (not just didactic markers,
 * but actually explains something meaningful).
 * NEW: Stricter than isDidactic — requires real educational content.
 */
export function isSubstantiveExplanation(
  text: string,
  module: string,
): boolean {
  const trimmed = text.trim()
  if (trimmed.length < 40) return false
  if (isGenericText(trimmed, GENERIC_EXPLANATION_PATTERNS)) return false

  // Must have module-specific depth AND general didactic quality
  const hasModuleDepth = hasModuleSpecificDepth(trimmed, module)
  const hasDidacticQuality = isDidactic(trimmed)

  // For high-quality: need both module depth and didactic structure
  // For acceptable: need at least one plus sufficient length
  if (hasModuleDepth && hasDidacticQuality) return true
  if ((hasModuleDepth || hasDidacticQuality) && trimmed.length >= 60)
    return true

  return false
}

/**
 * Returns true if the explanation contains module-specific depth markers
 */
export function hasModuleSpecificDepth(text: string, module: string): boolean {
  switch (module) {
    case 'Getalbegrip':
      // FIXED: Also recognize comparison operators, rule references, and number line reasoning
      return (
        /\d+\s*[×x\*÷\/\+\-]\s*\d+/.test(text) ||
        text.includes('=') ||
        /-?\d+\s*[<>≤≥≠]\s*-?\d+/.test(text) ||
        /\bregel\s*:/i.test(text) ||
        /getallenlijn|links.*rechts|rechts.*links/i.test(text) ||
        /negatief|positief|minteken|plusteken/i.test(text) ||
        /kleiner|groter|gelijk/i.test(text)
      )
    case 'SpellingWW':
      return SWW_RULES.test(text)
    case 'SpellingRegel':
      return SPR_RULES.test(text)
    case 'Metriek':
      return MET_FORMULA.test(text)
    case 'Tijd':
      return (
        TGL_CALC.test(text) ||
        /minuten|uren|seconden|klok|kalender|snelheid/i.test(text)
      )
    case 'Geld':
      return (
        TGL_CALC.test(text) ||
        /euro|cent|wisselgeld|korting|btw|budget/i.test(text)
      )
    case 'Basisrekenen':
      return (
        /\d+\s*[×x\*÷\/\+\-]\s*\d+/.test(text) ||
        /strategie|splitsen|compenseren|rijgen|schatten/i.test(text)
      )
    case 'Taalbegrip':
      return TAA_CITE.test(text)
    case 'Studievaardig':
      return STU_STRATEGY.test(text)
    case 'Topo':
      return /provincie|hoofdstad|rivier|land|continent|oceaan|gebergte|zee|eiland|grens|stad|berg|kust|noorden|zuiden|oosten|westen|stroomt|ligt|grenst|klimaat/i.test(
        text,
      )
    default:
      return isDidactic(text)
  }
}

// ─── Tier-aware minimum requirements ──────────────────────────────────────────

/** Minimum explanation length by tier band */
export const TIER_MIN_EXPLANATION_LENGTH: Record<string, number> = {
  basic: 30, // Tiers 1-4
  intermediate: 50, // Tiers 5-8
  advanced: 80, // Tiers 9-12
  expert: 100, // Tiers 13-16
}

/** Minimum distractor explanation length by tier band */
export const TIER_MIN_DISTRACTOR_LENGTH: Record<string, number> = {
  basic: 15,
  intermediate: 25,
  advanced: 35,
  expert: 45,
}

export function getTierBandKey(tier: number): string {
  if (tier <= 4) return 'basic'
  if (tier <= 8) return 'intermediate'
  if (tier <= 12) return 'advanced'
  return 'expert'
}

// ─── Aggregation helpers ──────────────────────────────────────────────────────

export interface IssueSummary {
  critical: number
  warning: number
  info: number
  total: number
  blocking: number
}

export function summarizeIssues(
  issues: Array<{ severity: SeverityLevel }>,
): IssueSummary {
  const summary: IssueSummary = {
    critical: 0,
    warning: 0,
    info: 0,
    total: 0,
    blocking: 0,
  }
  for (const issue of issues) {
    summary[issue.severity]++
    summary.total++
    if (isBlockingSeverity(issue.severity)) summary.blocking++
  }
  return summary
}

export function computeQualityScore(
  issues: Array<{ severity: SeverityLevel }>,
  total: number,
): number {
  if (total === 0) return 100
  const penalty = issues.reduce(
    (acc, i) => acc + (SEVERITY_WEIGHTS[i.severity] ?? 0),
    0,
  )
  const maxPenalty = total * SEVERITY_WEIGHTS.critical
  return Math.max(0, Math.round(100 - (penalty / maxPenalty) * 100))
}

// ─── Jargon & Passive Voice Detection (CONTENT_GOVERNANCE_FRAMEWORK §1.1, §4.1) ──

/**
 * Forbidden jargon patterns for explanation and distractor text.
 * These terms are too complex for children aged 9-11 (groep 6-8).
 * Source: CONTENT_GOVERNANCE_FRAMEWORK.md §1.1 + Bijlage B
 *
 * REFINED v5.9: Removed "positie(s)" — standard Dutch word used in many
 * non-jargon contexts (e.g., "de positie van het cijfer"). Only flag
 * truly academic/technical terms that children wouldn't encounter.
 */
export const JARGON_PATTERNS: Array<{ pattern: RegExp; replacement: string }> =
  [
    { pattern: /\bstrategie\b/i, replacement: '"handige tip"' },
    { pattern: /\balgoritme\b/i, replacement: '"stappenplan"' },
    {
      pattern: /\bcommutatief\b/i,
      replacement: '"je mag de volgorde omdraaien"',
    },
    {
      pattern: /\bassociatief\b/i,
      replacement: '"je mag de haakjes verplaatsen"',
    },
    { pattern: /\bproportioneel\b/i, replacement: '"in verhouding"' },
    { pattern: /\bdistributief\b/i, replacement: '(avoid)' },
    { pattern: /\bexponentieel\b/i, replacement: '(avoid)' },
    { pattern: /\bnoemer\b/i, replacement: '"getal onder de streep"' },
    { pattern: /\bteller\b/i, replacement: '"getal boven de streep"' },
    { pattern: /\bdecimaalgetal\b/i, replacement: '"kommagetal"' },
  ]

/**
 * Passive voice patterns forbidden in child-facing explanations.
 * Source: CONTENT_GOVERNANCE_FRAMEWORK.md §4.1
 */
export const PASSIVE_VOICE_PATTERNS: RegExp[] = [
  /\ber wordt\b/i,
  /\ber worden\b/i,
  /\bhet wordt berekend\b/i,
  /\bmen kan\b/i,
  /\bwordt berekend\b/i,
  /\bwordt gevonden\b/i,
  /\bwordt bepaald\b/i,
  /\bwordt uitgerekend\b/i,
  /\bkan worden\b/i,
]

/**
 * Returns the maximum words per sentence for a given tier.
 * Source: CONTENT_GOVERNANCE_FRAMEWORK.md §1.2, §4.1
 *
 * REFINED v5.9: Raised limits to be realistic for Dutch educational
 * explanation text. Previous limits (12/15/18) were too strict and
 * generated excessive false positives on well-written explanations.
 * Dutch compound words and pedagogical phrasing naturally produce
 * longer sentences than English equivalents.
 */
export function getMaxWordsPerSentence(tier: number): number {
  if (tier <= 4) return 18 // groep 6 (was 12 — too strict for Dutch)
  if (tier <= 8) return 22 // groep 7 (was 15 — too strict for Dutch)
  return 25 // groep 8 (was 18 — too strict for Dutch)
}

/**
 * Returns sentences that exceed the tier-appropriate word limit.
 * Returns empty array if all sentences are within limits.
 */
export function detectLongSentences(text: string, tier: number): string[] {
  const maxWords = getMaxWordsPerSentence(tier)
  const sentences = text
    .split(/[.!?]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
  return sentences.filter((s) => s.split(/\s+/).length > maxWords)
}

/**
 * Returns matched jargon terms found in the text.
 * Returns empty array if no jargon detected.
 *
 * ENHANCED (GAP-P1/FP1/FP2): Accepts optional tier for tier-aware filtering.
 * "teller" and "noemer" are standard curriculum vocabulary for advanced+ tiers (9+)
 * and should only be flagged for basic band (tiers 1-4).
 */
export function detectJargon(
  text: string,
  tier?: number,
): Array<{ term: string; replacement: string }> {
  const found: Array<{ term: string; replacement: string }> = []
  for (const { pattern, replacement } of JARGON_PATTERNS) {
    const match = text.match(pattern)
    if (match) {
      // Tier-aware filtering: "teller" and "noemer" are standard math terms
      // for advanced+ tiers (9+), only flag for basic band (1-4)
      if (tier !== undefined && tier > 4) {
        const termLower = match[0].toLowerCase()
        if (termLower === 'teller' || termLower === 'noemer') {
          continue // Skip — standard vocabulary at this tier
        }
      }
      found.push({ term: match[0], replacement })
    }
  }
  return found
}

/**
 * Returns true if the text contains passive voice constructions.
 */
export function hasPassiveVoice(text: string): boolean {
  return PASSIVE_VOICE_PATTERNS.some((p) => p.test(text))
}
